import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type WorkspaceAccess = {
  admin: boolean;
  crm: boolean;
  smm: boolean;
  partner: boolean;
  business: boolean;
};

/** Which staff workspaces the signed-in user can actually open. */
export const getMyWorkspaceAccess = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<WorkspaceAccess> => {
    const { supabase, userId } = context as { supabase: any; userId: string };

    const settled = await Promise.allSettled([
      supabase.rpc("current_user_is_admin"),
      supabase.rpc("current_user_is_super_admin"),
      supabase.from("user_roles").select("role").eq("user_id", userId).eq("role", "sales_rep").maybeSingle(),
      supabase.from("partner_users").select("client_id").eq("user_id", userId).limit(1),
      supabase.rpc("is_partner", { _user_id: userId }),
      supabase.rpc("is_smm_member", { _user_id: userId }),
      supabase.rpc("is_smm_admin", { _user_id: userId }),
      supabase.from("admin_permissions").select("page_key, access").eq("user_id", userId),
    ]);
    const val = <T,>(i: number): T | null =>
      settled[i].status === "fulfilled" ? ((settled[i] as PromiseFulfilledResult<any>).value as T) : null;

    const adminRpc = val<{ data: boolean | null }>(0);
    const superRpc = val<{ data: boolean | null }>(1);
    const salesRow = val<{ data: unknown }>(2);
    const partnerRows = val<{ data: unknown[] | null }>(3);
    const partnerRpc = val<{ data: boolean | null }>(4);
    const smmMem = val<{ data: boolean | null }>(5);
    const smmAdm = val<{ data: boolean | null }>(6);
    const perms = val<{ data: { page_key: string; access: string }[] | null }>(7);

    const isAdmin = !!adminRpc?.data || !!superRpc?.data;
    const isSuper = !!superRpc?.data;
    const isSales = !!salesRow?.data;
    const isPartner = (partnerRows?.data ?? []).length > 0 || !!partnerRpc?.data;
    const isSmm = !!smmMem?.data || !!smmAdm?.data || isAdmin;

    let business = false;
    if (isAdmin) {
      const rows = perms?.data ?? [];
      if (isSuper || rows.length === 0) {
        business = true;
      } else {
        const row = rows.find((r) => r.page_key === "business");
        business = row?.access === "view" || row?.access === "edit";
      }
    }

    return {
      admin: isAdmin,
      crm: isAdmin || isSales,
      smm: isSmm,
      partner: isPartner,
      business,
    };
  });
